import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { degToRad, getEffectiveSoundVelocity } from "./sim-core.js";
import { getTerrainData, sampleSeafloorHeight } from "./terrain.js";
import { getTerrainExtents, getTerrainType, sampleTerrainHeight } from "./terrain-data.js";

const SOUNDING_RENDER_LIFT_M = 0.08;

export function createSceneView({ host, onCameraChange }) {
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  host.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const backgroundColor = new THREE.Color(0x061120);
  scene.background = backgroundColor;
  scene.fog = new THREE.Fog(backgroundColor.getHex(), 220, 2200);
  const defaultFogNear = 220;
  const defaultFogFar = 2200;

  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 14000);
  camera.position.set(95, 72, 120);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.12;
  controls.rotateSpeed = 0.45;
  controls.zoomSpeed = 0.55;
  controls.panSpeed = 0.55;
  controls.target.set(0, -30, 0);
  controls.minPolarAngle = 0;
  controls.maxPolarAngle = Math.PI;
  controls.minDistance = 2;
  controls.maxDistance = 9000;
  controls.addEventListener("change", () => onCameraChange?.());

  const hemi = new THREE.HemisphereLight(0x9dcbff, 0x7899bc, 0.95);
  scene.add(hemi);

  const ambient = new THREE.AmbientLight(0xb7d4f6, 0.52);
  scene.add(ambient);

  const sun = new THREE.DirectionalLight(0xffffff, 1.2);
  sun.position.set(40, 70, 30);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  scene.add(sun);

  const fill = new THREE.DirectionalLight(0xa9d4ff, 0.5);
  fill.position.set(-32, -26, -22);
  scene.add(fill);

  const headlight = new THREE.PointLight(0xbfdfff, 0.45, 700);
  headlight.position.set(0, 0, 0);
  camera.add(headlight);
  scene.add(camera);

  const waterPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(3200, 3200),
    new THREE.MeshPhongMaterial({
      color: 0x1a3f63,
      transparent: true,
      opacity: 0.37,
      side: THREE.DoubleSide,
    }),
  );
  waterPlane.rotateX(-Math.PI / 2);
  waterPlane.position.y = -0.05;
  scene.add(waterPlane);

  const underwaterVisualGroup = new THREE.Group();
  underwaterVisualGroup.position.y = 0;
  underwaterVisualGroup.scale.set(1, 1, 1);
  scene.add(underwaterVisualGroup);

  let vessel = createVessel("survey_launch");
  scene.add(vessel);
  const transducerWorld = new THREE.Vector3();

  const fanMesh = new THREE.Mesh(
    new THREE.BufferGeometry(),
    new THREE.MeshPhongMaterial({
      color: 0xffa450,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
    }),
  );
  underwaterVisualGroup.add(fanMesh);

  const fanRays = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0xffcd8b }),
  );
  underwaterVisualGroup.add(fanRays);
  const swathOutline = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0x000000 }),
  );
  underwaterVisualGroup.add(swathOutline);

  const pointSprite = createCircleSpriteTexture();
  const soundingPoints = new THREE.Points(
    new THREE.BufferGeometry(),
    new THREE.PointsMaterial({
      color: 0xfff3b0,
      size: 3.2,
      sizeAttenuation: false,
      vertexColors: true,
      map: pointSprite,
      transparent: true,
      alphaTest: 0.5,
      depthTest: false,
      depthWrite: false,
    }),
  );
  soundingPoints.renderOrder = 60;
  underwaterVisualGroup.add(soundingPoints);
  const liveHitCapacity = 4096;
  const livePingBuffers = createLivePingBuffers(fanMesh, fanRays, soundingPoints, liveHitCapacity);

  const soundingHistoryMaterial = new THREE.PointsMaterial({
    color: 0x7cc6ff,
    size: 2.4,
    sizeAttenuation: false,
    vertexColors: true,
    map: pointSprite,
    transparent: true,
    alphaTest: 0.15,
    opacity: 0.6,
    depthTest: false,
    depthWrite: false,
  });
  const soundingHistoryRoot = new THREE.Group();
  underwaterVisualGroup.add(soundingHistoryRoot);

  const meshGrid = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({
      color: 0x9ad7f2,
      transparent: true,
      opacity: 0.55,
    }),
  );
  underwaterVisualGroup.add(meshGrid);
  const gridNodeCellGeometry = new THREE.PlaneGeometry(1, 1);
  gridNodeCellGeometry.rotateX(-Math.PI / 2);
  const gridNodeCellMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide,
    depthWrite: false,
    depthTest: false,
  });
  const GRID_NODE_CELL_CAPACITY = 400000;
  const gridNodeCells = new THREE.InstancedMesh(gridNodeCellGeometry, gridNodeCellMaterial, GRID_NODE_CELL_CAPACITY);
  gridNodeCells.count = 0;
  gridNodeCells.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  gridNodeCells.frustumCulled = false;
  gridNodeCells.renderOrder = 40;
  underwaterVisualGroup.add(gridNodeCells);
  const gridSurfaceMaterialFlat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    vertexColors: true,
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide,
    depthWrite: false,
    depthTest: false,
  });
  const gridSurfaceMaterialShaded = new THREE.MeshLambertMaterial({
    color: 0xffffff,
    vertexColors: true,
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide,
    depthWrite: false,
    depthTest: false,
  });
  const gridSurfaceMesh = new THREE.Mesh(new THREE.BufferGeometry(), gridSurfaceMaterialShaded);
  gridSurfaceMesh.frustumCulled = false;
  gridSurfaceMesh.renderOrder = 39;
  underwaterVisualGroup.add(gridSurfaceMesh);
  const missionPlanLines = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({
      color: 0x8ff3c8,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
    }),
  );
  scene.add(missionPlanLines);

  const travelTimeRing = new THREE.Mesh(
    new THREE.RingGeometry(10, 11.5, 96),
    new THREE.MeshBasicMaterial({
      color: 0x8ed9ff,
      transparent: true,
      opacity: 0.45,
      side: THREE.DoubleSide,
    }),
  );
  travelTimeRing.rotation.x = -Math.PI / 2;
  travelTimeRing.position.y = 0.02;
  scene.add(travelTimeRing);
  const courseIndicatorDir = new THREE.Vector3(1, 0, 0);
  const courseIndicator = new THREE.ArrowHelper(courseIndicatorDir, new THREE.Vector3(0, 0.08, 0), 42, 0xb9ff7d, 7, 3.6);
  scene.add(courseIndicator);

  let seafloor = null;
  let seafloorPreset = "";
  let terrainRevision = -1;
  let waterExtent = 3200;
  let wreckMesh = null;
  let wreckGeometry = null;
  let wreckSourceVertices = null;
  let wreckMeshVersion = 0;
  let wreckBaseScale = 1;
  let wreckBaseHalfHeight = 4;
  const stlLoader = new STLLoader();
  let lastGeometryKey = "";
  let lastSnapshotSeq = -1;
  let lastHistoryPing = -1;
  let lastHistorySeq = -1;
  let lastHistoryColorKey = "";
  let lastMeshGridKey = "";
  let lastMissionVersion = -1;
  let lastGridNodesKey = "";
  let lastTriSurfaceBuildAt = 0;
  let boatShape = "survey_launch";
  const historyChunks = [];
  const historyChunkPointCapacity = 8192;
  let totalHistoryPoints = 0;
  let sourceDirty = true;
  let fanDirty = true;
  let cameraMode = "free";
  let missionLockAoi = true;
  let waterBaseOpacity = 0.37;
  let trackInitialized = false;
  const trackedVesselPos = new THREE.Vector3();
  const tmpCameraDelta = new THREE.Vector3();
  const tmpForward = new THREE.Vector3();
  const tmpRight = new THREE.Vector3();

  function setState(state, snapshot = null, density = null, mission = null, snapshotBatch = null) {
    const verticalExaggeration = Math.max(1, Math.min(8, Number(state.verticalExaggeration) || 1));
    underwaterVisualGroup.scale.y = verticalExaggeration;
    updateHillshadeLight(sun, state);

    if (seafloorPreset !== state.seafloorPreset || terrainRevision !== state.terrainRevision) {
      if (seafloor) {
        underwaterVisualGroup.remove(seafloor);
        seafloor.geometry.dispose();
        seafloor.material.dispose();
      }
      seafloor = createSeafloor(state.seafloorPreset, state.range);
      underwaterVisualGroup.add(seafloor);
      updateWaterSurfaceExtent(state.seafloorPreset);
      seafloorPreset = state.seafloorPreset;
      terrainRevision = state.terrainRevision;
      sourceDirty = true;
      fanDirty = true;
      lastMeshGridKey = "";
    }

    const geometryKey = [
      state.accuracyMode,
      state.raytraceMode,
      state.soundingSolutionMode,
      state.beamformingSvUsed,
      state.beamformingSvTrue,
      state.svpMode,
      state.svSurfaceVelocity,
      state.svDeepVelocity,
      state.svTransitionDepth,
      state.trueSvpMode,
      state.trueSvSurfaceVelocity,
      state.trueSvDeepVelocity,
      state.trueSvTransitionDepth,
      state.trueSoundVelocity,
      state.range,
      state.beamAngle,
      state.beamCount,
      state.beamSpacingMode,
      state.pulseWidth,
      state.soundVelocity,
      state.rollEnabled,
      state.roll,
      state.pitchEnabled,
      state.pitch,
      state.yawEnabled,
      state.yaw,
      state.seafloorPreset,
      state.terrainRevision,
      state.soundingColorMode,
      state.soundingColorRamp,
      state.soundingColorInvert,
      state.soundingDepthMode,
      state.soundingDepthMin,
      state.soundingDepthMax,
      state.soundingPointsColor,
      state.wreckEnabled,
      state.wreckVisible,
      state.wreckScale,
      state.wreckOffsetX,
      state.wreckOffsetY,
      state.wreckOffsetZ,
      state.wreckYaw,
      state.wreckRoll,
      state.wreckPitch,
      state.wreckEmbed,
    ].join("|");
    if (geometryKey !== lastGeometryKey) {
      sourceDirty = true;
      fanDirty = true;
      lastGeometryKey = geometryKey;
    }
    if (snapshot && snapshot.seq !== lastSnapshotSeq) {
      fanDirty = true;
      lastSnapshotSeq = snapshot.seq;
    }
    if (state.cameraMode !== cameraMode) {
      cameraMode = state.cameraMode;
      trackInitialized = false;
      if (cameraMode !== "follow") {
        trackInitialized = false;
      }
      if (cameraMode === "mission") {
        frameAoiInView(true);
      }
    }
    applyVisibilityTuning();
    missionLockAoi = state.missionLockAoi !== false;
    controls.enabled = !(cameraMode === "mission" && missionLockAoi);

    const meshGridKey = [
      state.meshGridEnabled,
      state.meshGridSpacing,
      state.seafloorPreset,
      state.terrainRevision,
    ].join("|");
    if (meshGridKey !== lastMeshGridKey) {
      updateMeshGridGeometry(meshGrid, state);
      lastMeshGridKey = meshGridKey;
    }
    const missionVersion = Number(mission?.version) || 0;
    if (missionVersion !== lastMissionVersion) {
      updateMissionPlanGeometry(missionPlanLines, mission?.segments || []);
      lastMissionVersion = missionVersion;
    }
    const densityVersion = Number(density?.version) || 0;
    const triMode = state.gridSurfaceMode === "triangulated";
    const triVersionBucket = triMode ? Math.floor(densityVersion / 5) : densityVersion;
    const nowMs = performance.now();
    const triRebuildAllowed = !triMode || ((nowMs - lastTriSurfaceBuildAt) >= 220);
    const gridNodesKey = [
      triVersionBucket,
      state.gridNodesEnabled ? 1 : 0,
      state.gridSurfaceMode || "triangulated",
      state.gridSurfaceHillshade ? 1 : 0,
      state.gridSurfaceMaskNoData ? 1 : 0,
      state.gridNodeMetric || "mean",
      state.gridNodeColorRamp || "viridis",
      Number(state.gridNodeOpacity) || 0,
      Number(state.gridNodeSize) || 3,
    ].join("|");
    const gridNodesNeedUpdate = gridNodesKey !== lastGridNodesKey;
    if (gridNodesNeedUpdate && triMode && !triRebuildAllowed) {
      // Throttle triangulated surface rebuilds without mutating key state between frames.
    } else if (gridNodesNeedUpdate) {
      updateGridNodeGeometry(
        gridNodeCells,
        gridNodeCellMaterial,
        gridSurfaceMesh,
        gridSurfaceMaterialFlat,
        gridSurfaceMaterialShaded,
        density,
        state,
        GRID_NODE_CELL_CAPACITY,
      );
      lastGridNodesKey = gridNodesKey;
      if (triMode) {
        lastTriSurfaceBuildAt = nowMs;
      }
    }
    if (state.boatShape !== boatShape) {
      swapVessel(state.boatShape || "survey_launch");
      boatShape = state.boatShape || "survey_launch";
      sourceDirty = true;
      fanDirty = true;
    }
    syncWreckMesh(state);

    if (snapshot?.quaternion) {
      vessel.quaternion.set(snapshot.quaternion[0], snapshot.quaternion[1], snapshot.quaternion[2], snapshot.quaternion[3]);
      vessel.position.set(snapshot.origin[0], 0, snapshot.origin[2]);
    } else {
      vessel.rotation.order = "YXZ";
      vessel.rotation.y = degToRad(-(state.steerage || 0));
      vessel.rotation.x = degToRad(state.rollEnabled === false ? 0 : state.roll);
      vessel.rotation.z = degToRad(state.pitchEnabled === false ? 0 : state.pitch);
      vessel.position.set(0, 0, 0);
    }
    vessel.updateMatrixWorld(true);
    seafloor.updateMatrixWorld(true);
    scene.updateMatrixWorld(true);

    if (snapshot?.origin) {
      transducerWorld.set(snapshot.origin[0], snapshot.origin[1], snapshot.origin[2]);
    } else {
      const transducer = vessel.getObjectByName("transducer");
      transducer.getWorldPosition(transducerWorld);
    }
    updateSwathOutlineFromState(swathOutline, snapshot, state, transducerWorld, vessel.quaternion);

    const height = state.range;
    const radius = Math.tan(degToRad(state.beamAngle / 2)) * height;
    const freqNorm = clamp01((state.frequency - 30) / (700 - 30));
    const pulseNorm = clamp01((state.pulseWidth - 0.1) / (20 - 0.1));
    const effectiveSv = getEffectiveSoundVelocity(state, state.range);
    const soundVelocityNorm = clamp01((effectiveSv - 1450) / (1550 - 1450));
    const pulseThickness = Math.max(0.45, radius * (0.02 + pulseNorm * 0.16));

    if (sourceDirty) {
      sourceDirty = false;
    }

    if (fanDirty && (state.showSwathFill || state.showSwathBeams || state.showSoundings)) {
      if (snapshot?.hits?.length) {
        updateFanGeometryFromSnapshot(snapshot, livePingBuffers, state);
      } else {
        clearFanGeometry(livePingBuffers);
      }
      fanDirty = false;
    }

    const historySnapshots = Array.isArray(snapshotBatch) && snapshotBatch.length
      ? snapshotBatch
      : (snapshot ? [snapshot] : []);
    let soundingDepthWindow = getSoundingDepthWindow(state, livePingBuffers, historyChunks);
    let historyColorKey = getSoundingColorKey(state, soundingDepthWindow);
    if (historySnapshots.length) {
      if (historyColorKey !== lastHistoryColorKey && totalHistoryPoints > 0) {
        recolorHistoryChunks(historyChunks, state, soundingDepthWindow);
        recolorLiveSoundings(livePingBuffers, state, soundingDepthWindow);
      }
      const requested = Math.floor(Number(state.historyLength) || 0);
      const maxHistoryPoints = state.historyUnlimited
        ? Infinity
        : (requested > 0 ? Math.max(64, requested * Math.min(state.beamCount, 256)) : Infinity);
      for (let i = 0; i < historySnapshots.length; i += 1) {
        const hs = historySnapshots[i];
        if (hs?.resetSnapshot) {
          clearSoundingHistory(historyChunks, soundingHistoryRoot);
          totalHistoryPoints = 0;
          lastHistoryPing = -1;
          lastHistorySeq = -1;
          lastHistoryColorKey = "";
          continue;
        }
        const pingIndex = Number(hs?.pingIndex);
        const seqIndex = Number(hs?.seq);
        const newByPing = Number.isFinite(pingIndex) && pingIndex !== lastHistoryPing;
        const newBySeq = Number.isFinite(seqIndex) && seqIndex !== lastHistorySeq;
        if (hs?.hits?.length && (newByPing || newBySeq)) {
          const historyStride = getHistoryDisplayHitStride(hs.hits.length);
          totalHistoryPoints = appendSoundingHistoryChunked(
            hs.hits,
            historyChunks,
            soundingHistoryRoot,
            soundingHistoryMaterial,
            historyChunkPointCapacity,
            totalHistoryPoints,
            maxHistoryPoints,
            state,
            historyStride,
          );
          if (Number.isFinite(pingIndex)) lastHistoryPing = pingIndex;
          if (Number.isFinite(seqIndex)) lastHistorySeq = seqIndex;
        }
      }
      soundingDepthWindow = getSoundingDepthWindow(state, livePingBuffers, historyChunks);
      historyColorKey = getSoundingColorKey(state, soundingDepthWindow);
      if (historyColorKey !== lastHistoryColorKey) {
        recolorHistoryChunks(historyChunks, state, soundingDepthWindow);
        recolorLiveSoundings(livePingBuffers, state, soundingDepthWindow);
      }
      lastHistoryColorKey = historyColorKey;
    } else if (totalHistoryPoints > 0) {
      if (historyColorKey !== lastHistoryColorKey) {
        recolorHistoryChunks(historyChunks, state, soundingDepthWindow);
        recolorLiveSoundings(livePingBuffers, state, soundingDepthWindow);
        lastHistoryColorKey = historyColorKey;
      }
    }

    const swathFillOpacity = clamp01(state.swathFillOpacity ?? 0.35);
    const swathBeamsOpacity = clamp01(state.swathBeamsOpacity ?? 1);
    const soundingsOpacity = clamp01(state.soundingsOpacity ?? 1);
    const historyOpacity = clamp01(state.historySoundingsOpacity ?? 0.6);
    const boatOpacity = clamp01(state.boatOpacity ?? 1);
    waterBaseOpacity = clamp01(state.waterSurfaceOpacity ?? 0.37);
    const seafloorOpacity = clamp01(state.seafloorOpacity ?? 1);
    const meshGridOpacity = clamp01(state.meshGridOpacity ?? 0.55);

    fanMesh.material.opacity = swathFillOpacity * (0.8 + (1 - freqNorm) * 0.2);
    fanMesh.material.transparent = fanMesh.material.opacity < 0.999;
    fanMesh.material.color.set(state.swathFillColor);
    fanRays.material.color.set(state.swathBeamsColor);
    fanRays.material.opacity = swathBeamsOpacity;
    fanRays.material.transparent = fanRays.material.opacity < 0.999;
    swathOutline.material.color.set(state.swathOutlineColor || "#000000");
    swathOutline.material.opacity = clamp01(state.swathOutlineOpacity ?? 0.9);
    swathOutline.material.transparent = swathOutline.material.opacity < 0.999;
    soundingPoints.material.size = 2.2 + (1 - freqNorm) * 2.1 + pulseNorm * 1.2;
    soundingPoints.material.color.set("#ffffff");
    soundingPoints.material.opacity = soundingsOpacity;
    soundingHistoryMaterial.color.set(state.historySoundingsColor);
    soundingHistoryMaterial.opacity = historyOpacity;
    waterPlane.material.color.set(state.waterSurfaceColor);
    waterPlane.material.opacity = waterBaseOpacity;
    seafloor.material.color.set(state.seafloorColor);
    seafloor.material.opacity = seafloorOpacity;
    seafloor.material.transparent = seafloorOpacity < 0.999;
    meshGrid.material.color.set(state.meshGridColor || "#9ad7f2");
    meshGrid.material.opacity = meshGridOpacity;
    meshGrid.material.transparent = meshGridOpacity < 0.999;
    setVesselColor(vessel, state.boatColor);
    setVesselOpacity(vessel, boatOpacity);
    if (typeof state.backgroundColor === "string") {
      backgroundColor.set(state.backgroundColor);
      scene.background = backgroundColor;
      scene.fog.color.copy(backgroundColor);
    }

    travelTimeRing.geometry.dispose();
    const ringRadius = Math.max(8, effectiveSv * 0.03);
    travelTimeRing.geometry = new THREE.RingGeometry(ringRadius, ringRadius + 1.35 + pulseNorm * 2.2, 96);
    travelTimeRing.position.set(transducerWorld.x, 0.02, transducerWorld.z);
    travelTimeRing.material.opacity = 0.25 + soundVelocityNorm * 0.25;
    travelTimeRing.material.color.setHSL(0.56 + soundVelocityNorm * 0.08, 0.88, 0.66);
    const courseHeading = Number(snapshot?.courseHeading ?? (-(state.course || 0) - (state.steerage || 0))) || 0;
    const courseRad = degToRad(courseHeading);
    courseIndicatorDir.set(Math.cos(courseRad), 0, -Math.sin(courseRad));
    courseIndicator.setDirection(courseIndicatorDir);
    courseIndicator.position.set(vessel.position.x, 0.08, vessel.position.z);

    vessel.visible = state.showBoat;
    waterPlane.visible = state.showWaterSurface;
    travelTimeRing.visible = state.showWaterSurface;
    seafloor.visible = state.showSeafloor;
    meshGrid.visible = state.meshGridEnabled;
    gridNodeCells.visible = Boolean(state.gridNodesEnabled && state.gridSurfaceMode !== "triangulated");
    gridSurfaceMesh.visible = Boolean(state.gridNodesEnabled && state.gridSurfaceMode === "triangulated");
    fanMesh.visible = state.showSwathFill;
    fanRays.visible = state.showSwathBeams;
    swathOutline.visible = state.showSwathOutline !== false;
    soundingPoints.visible = state.showSoundings;
    soundingHistoryRoot.visible = state.showHistorySoundings;
    missionPlanLines.visible = Array.isArray(mission?.segments) && mission.segments.length > 0;
  }

  function render(time) {
    waterPlane.material.opacity = clamp01(waterBaseOpacity + Math.sin(time * 0.0014) * Math.min(0.04, waterBaseOpacity * 0.18));
    if (cameraMode === "follow") {
      updateTrackedCamera();
    } else if (cameraMode === "mission" && missionLockAoi) {
      frameAoiInView();
    }
    controls.update();
    renderer.render(scene, camera);
  }

  function applyVisibilityTuning() {
    if (cameraMode === "mission") {
      scene.fog.near = 4000;
      scene.fog.far = 16000;
      camera.far = 20000;
      controls.maxDistance = 14000;
    } else {
      scene.fog.near = defaultFogNear;
      scene.fog.far = defaultFogFar;
      camera.far = 14000;
      controls.maxDistance = 9000;
    }
    camera.updateProjectionMatrix();
  }

  function resize() {
    const { clientWidth, clientHeight } = host;
    renderer.setSize(clientWidth, clientHeight);
    camera.aspect = clientWidth / Math.max(clientHeight, 1);
    camera.updateProjectionMatrix();
  }

  function getCameraPose() {
    return {
      position: camera.position.clone(),
      quaternion: camera.quaternion.clone(),
      target: controls.target.clone(),
    };
  }

  function setCameraPose(pose) {
    camera.position.copy(pose.position);
    camera.quaternion.copy(pose.quaternion);
    controls.target.copy(pose.target);
    camera.updateMatrixWorld();
    controls.update();
  }

  function updateTrackedCamera() {
    if (!trackInitialized) {
      trackedVesselPos.copy(vessel.position);
      trackInitialized = true;
      return;
    }

    const dx = vessel.position.x - trackedVesselPos.x;
    const dy = vessel.position.y - trackedVesselPos.y;
    const dz = vessel.position.z - trackedVesselPos.z;
    if (Math.abs(dx) + Math.abs(dy) + Math.abs(dz) < 1e-6) {
      return;
    }
    camera.position.x += dx;
    camera.position.y += dy;
    camera.position.z += dz;
    controls.target.x += dx;
    controls.target.y += dy;
    controls.target.z += dz;
    trackedVesselPos.copy(vessel.position);
  }

  function recenterCamera() {
    tmpCameraDelta.copy(camera.position).sub(controls.target);
    if (tmpCameraDelta.lengthSq() < 1e-6) {
      tmpCameraDelta.set(95, 72, 120);
    }
    camera.position.copy(vessel.position).add(tmpCameraDelta);
    controls.target.copy(vessel.position);
    if (cameraMode === "follow") {
      trackedVesselPos.copy(vessel.position);
      trackInitialized = true;
    }
    controls.update();
  }

  function snapCameraPreset(preset = "birdsEye") {
    vessel.updateMatrixWorld(true);
    tmpForward.set(1, 0, 0).applyQuaternion(vessel.quaternion).setY(0);
    if (tmpForward.lengthSq() < 1e-8) {
      tmpForward.set(1, 0, 0);
    } else {
      tmpForward.normalize();
    }
    tmpRight.set(-tmpForward.z, 0, tmpForward.x);
    const target = vessel.position.clone();
    let desired = target.clone();
    if (preset === "obliqueBehind") {
      desired
        .addScaledVector(tmpForward, -120)
        .addScaledVector(tmpRight, 36);
      desired.y += 68;
    } else if (preset === "obliqueAhead") {
      desired
        .addScaledVector(tmpForward, 120)
        .addScaledVector(tmpRight, -34);
      desired.y += 70;
    } else {
      desired
        .addScaledVector(tmpForward, 0)
        .addScaledVector(tmpRight, 0);
      desired.y += 185;
    }
    camera.position.copy(desired);
    controls.target.copy(target);
    camera.lookAt(target);
    trackedVesselPos.copy(vessel.position);
    trackInitialized = true;
    controls.update();
  }

  function swapVessel(shape) {
    const previous = vessel;
    const next = createVessel(shape);
    next.position.copy(previous.position);
    next.quaternion.copy(previous.quaternion);
    next.userData.baseColor = previous.userData.baseColor;
    scene.add(next);
    scene.remove(previous);
    disposeGroupMaterials(previous);
    vessel = next;
  }

  function reframeAoi() {
    frameAoiInView(true);
  }

  function updateWaterSurfaceExtent(preset = seafloorPreset) {
    const ext = getSeafloorExtents(preset);
    const spanX = Math.abs(ext.maxX - ext.minX);
    const spanZ = Math.abs(ext.maxZ - ext.minZ);
    const target = Math.max(3200, Math.max(spanX, spanZ) * 1.25);
    if (Math.abs(target - waterExtent) < 1) {
      return;
    }
    waterExtent = target;
    waterPlane.geometry.dispose();
    waterPlane.geometry = new THREE.PlaneGeometry(waterExtent, waterExtent);
  }

  function frameAoiInView(force = false) {
    const ext = getSeafloorExtents(seafloorPreset);
    const minX = ext.minX;
    const maxX = ext.maxX;
    const minZ = ext.minZ;
    const maxZ = ext.maxZ;
    const terrain = seafloorPreset === "etopo_aoi" ? getTerrainData() : null;
    const minY = terrain && Number.isFinite(Number(terrain.minDepth)) ? Number(terrain.minDepth) : -80;
    const maxY = terrain && Number.isFinite(Number(terrain.maxDepth)) ? Number(terrain.maxDepth) : 0;

    const center = new THREE.Vector3((minX + maxX) * 0.5, (minY + maxY) * 0.5, (minZ + maxZ) * 0.5);
    const spanX = Math.max(1, Math.abs(maxX - minX));
    const spanZ = Math.max(1, Math.abs(maxZ - minZ));
    const spanY = Math.max(1, Math.abs(maxY - minY));
    const radius = 0.5 * Math.hypot(spanX, spanZ, spanY);
    const fovRad = (camera.fov * Math.PI) / 180;
    const dist = Math.max(120, (radius / Math.tan(fovRad * 0.5)) * 1.15);
    const dir = new THREE.Vector3(0.9, 0.82, 0.9).normalize();
    const camPos = center.clone().addScaledVector(dir, dist);

    if (!force) {
      const posErr = camera.position.distanceTo(camPos);
      const targetErr = controls.target.distanceTo(center);
      if (posErr < 0.2 && targetErr < 0.2) {
        return;
      }
    }

    camera.position.copy(camPos);
    controls.target.copy(center);
    camera.lookAt(center);
  }

  async function loadWreckStlFile(file) {
    const buf = await file.arrayBuffer();
    const parsed = stlLoader.parse(buf);
    parsed.computeVertexNormals();
    parsed.computeBoundingBox();
    let bbox = parsed.boundingBox;
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    bbox.getCenter(center);
    parsed.translate(-center.x, -center.y, -center.z);
    // STL assets are commonly authored Z-up; convert to this scene's Y-up coordinates.
    parsed.rotateX(-Math.PI / 2);
    parsed.computeBoundingBox();
    bbox = parsed.boundingBox;
    bbox.getSize(size);

    const longest = Math.max(1, size.x, size.y, size.z);
    const targetLength = 240;
    wreckBaseScale = targetLength / longest;
    wreckBaseHalfHeight = (size.y * 0.5 * wreckBaseScale) || 4;

    if (wreckGeometry) {
      wreckGeometry.dispose();
    }
    wreckGeometry = parsed;
    const posAttr = parsed.getAttribute("position");
    wreckSourceVertices = posAttr?.array ? new Float32Array(posAttr.array) : null;
    wreckMeshVersion += 1;
    if (wreckMesh) {
      underwaterVisualGroup.remove(wreckMesh);
      wreckMesh.material.dispose();
      wreckMesh = null;
    }
    return {
      length: size.x * wreckBaseScale,
      beam: size.z * wreckBaseScale,
      height: size.y * wreckBaseScale,
      sourceTriangles: Math.floor((wreckSourceVertices?.length || 0) / 9),
    };
  }

  function clearWreckStl() {
    if (wreckMesh) {
      underwaterVisualGroup.remove(wreckMesh);
      wreckMesh.material.dispose();
      wreckMesh = null;
    }
    if (wreckGeometry) {
      wreckGeometry.dispose();
      wreckGeometry = null;
    }
    wreckSourceVertices = null;
    wreckMeshVersion += 1;
  }

  function syncWreckMesh(state) {
    if (!wreckGeometry || !state.wreckEnabled) {
      if (wreckMesh) {
        wreckMesh.visible = false;
      }
      return;
    }
    if (!wreckMesh) {
      wreckMesh = new THREE.Mesh(
        wreckGeometry,
        new THREE.MeshStandardMaterial({
          color: 0x9aa7b4,
          metalness: 0.22,
          roughness: 0.76,
          transparent: true,
          opacity: 0.95,
        }),
      );
      wreckMesh.castShadow = true;
      wreckMesh.receiveShadow = true;
      underwaterVisualGroup.add(wreckMesh);
    }
    wreckMesh.visible = state.wreckVisible !== false;
    const x = Number(state.wreckOffsetX) || 0;
    const zHorizontal = Number(state.wreckOffsetY) || 0;
    const zVertical = Number(state.wreckOffsetZ) || 0;
    const userScale = Math.max(0.05, Number(state.wreckScale) || 1);
    const scale = wreckBaseScale * userScale;
    const halfHeight = wreckBaseHalfHeight * userScale;
    const bottom = sampleSeafloorHeight(state.seafloorPreset, state.range, x, zHorizontal);
    const embed = Math.max(0, Number(state.wreckEmbed) || 0);
    wreckMesh.position.set(x, bottom + halfHeight - embed + zVertical, zHorizontal);
    wreckMesh.rotation.order = "YXZ";
    wreckMesh.rotation.y = degToRad(Number(state.wreckYaw) || 0);
    wreckMesh.rotation.x = degToRad(Number(state.wreckRoll) || 0);
    wreckMesh.rotation.z = degToRad(Number(state.wreckPitch) || 0);
    wreckMesh.scale.setScalar(scale);
  }

  return {
    setState,
    render,
    resize,
    getCameraPose,
    setCameraPose,
    recenterCamera,
    snapCameraPreset,
    reframeAoi,
    loadWreckStlFile,
    clearWreckStl,
    getWreckMeshVersion: () => wreckMeshVersion,
    buildWreckWorkerPayload(maxTriangles = 300000) {
      if (!wreckSourceVertices || wreckSourceVertices.length < 9) {
        return null;
      }
      const triangles = Math.max(20000, Math.min(1000000, Math.floor(Number(maxTriangles) || 300000)));
      const vertices = buildDecimatedTriangleVerticesFromArray(wreckSourceVertices, triangles);
      return {
        vertices,
        baseScale: wreckBaseScale,
        baseHalfHeight: wreckBaseHalfHeight,
      };
    },
  };
}

function updateHillshadeLight(light, state) {
  if (!light) return;
  const azimuthDeg = ((Number(state?.gridHillshadeAzimuth) % 360) + 360) % 360;
  const elevationDeg = Math.max(1, Math.min(89, Number(state?.gridHillshadeElevation) || 45));
  const az = degToRad(azimuthDeg);
  const el = degToRad(elevationDeg);
  const radius = 90;
  const horizontal = Math.cos(el) * radius;
  light.position.set(
    Math.sin(az) * horizontal,
    Math.sin(el) * radius,
    Math.cos(az) * horizontal,
  );
}

function updateSwathOutlineFromState(swathOutline, snapshot, state, originVec3, fallbackQuat) {
  const halfAngle = degToRad((Number(state.beamAngle) || 0) * 0.5);
  const range = Math.max(0.1, Number(state.range) || 0.1);
  const beamQ = new THREE.Quaternion();
  if (Array.isArray(snapshot?.beamQuaternion) && snapshot.beamQuaternion.length >= 4) {
    beamQ.set(
      Number(snapshot.beamQuaternion[0]) || 0,
      Number(snapshot.beamQuaternion[1]) || 0,
      Number(snapshot.beamQuaternion[2]) || 0,
      Number(snapshot.beamQuaternion[3]) || 1,
    );
  } else {
    beamQ.copy(fallbackQuat || new THREE.Quaternion());
  }

  const origin = new THREE.Vector3(
    Number(originVec3?.x) || 0,
    Number(originVec3?.y) || 0,
    Number(originVec3?.z) || 0,
  );

  // Outline uses the range-depth plane: y reaches exactly `range` below origin at nadir.
  const portLocal = new THREE.Vector3(0, -range, Math.tan(-halfAngle) * range);
  const stbdLocal = new THREE.Vector3(0, -range, Math.tan(halfAngle) * range);
  const portVec = portLocal.clone().applyQuaternion(beamQ);
  const stbdVec = stbdLocal.clone().applyQuaternion(beamQ);
  const portDir = portVec.clone().normalize();
  const stbdDir = stbdVec.clone().normalize();
  if (!Number.isFinite(portDir.x + portDir.y + portDir.z + stbdDir.x + stbdDir.y + stbdDir.z)) {
    swathOutline.geometry.setDrawRange(0, 0);
    return;
  }

  const portEnd = origin.clone().add(portVec);
  const stbdEnd = origin.clone().add(stbdVec);
  const next = [
    origin.x, origin.y, origin.z,
    portEnd.x, portEnd.y, portEnd.z,
    origin.x, origin.y, origin.z,
    stbdEnd.x, stbdEnd.y, stbdEnd.z,
    portEnd.x, portEnd.y, portEnd.z,
    stbdEnd.x, stbdEnd.y, stbdEnd.z,
  ];
  let posAttr = swathOutline.geometry.getAttribute("position");
  if (!posAttr || !posAttr.array || posAttr.array.length !== 18) {
    swathOutline.geometry.dispose();
    const geom = new THREE.BufferGeometry();
    posAttr = new THREE.BufferAttribute(new Float32Array(18), 3);
    posAttr.setUsage(THREE.DynamicDrawUsage);
    geom.setAttribute("position", posAttr);
    swathOutline.geometry = geom;
  }
  for (let i = 0; i < 18; i += 1) {
    posAttr.array[i] = next[i];
  }
  posAttr.needsUpdate = true;
  swathOutline.geometry.setDrawRange(0, 6);
}

function updateGridNodeGeometry(
  gridNodeCells,
  gridNodeCellMaterial,
  gridSurfaceMesh,
  gridSurfaceMaterialFlat,
  gridSurfaceMaterialShaded,
  density,
  state,
  capacity = 200000,
) {
  if (!density || !density.gridNodesEnabled || !(density.stats instanceof Map) || density.stats.size === 0) {
    gridNodeCells.count = 0;
    gridSurfaceMesh.geometry.dispose();
    gridSurfaceMesh.geometry = new THREE.BufferGeometry();
    return;
  }
  const entries = Array.from(density.stats.entries());
  const limit = Math.max(1, Number(capacity) || 200000);
  const metric = String(density.gridNodeMetric || "mean");
  const ramp = String(density.gridNodeColorRamp || "viridis");
  const mode = String(state.gridSurfaceMode || "triangulated");
  const coarseTileMode = mode !== "triangulated" && entries.length > limit;
  if (coarseTileMode) {
    renderCoarseAggregatedTiles(
      gridNodeCells,
      gridNodeCellMaterial,
      gridSurfaceMesh,
      density,
      state,
      entries,
      metric,
      ramp,
      limit,
    );
    return;
  }

  let minV = Infinity;
  let maxV = -Infinity;
  const maxEntries = Math.min(entries.length, limit);
  const sampledIndices = samplePositionsByHashedModulo(
    entries.length,
    maxEntries,
    (i) => Number(entries[i]?.[0]) || 0,
  );
  const sampledValues = [];
  for (let si = 0; si < sampledIndices.length; si += 1) {
    const i = sampledIndices[si];
    const s = entries[i][1];
    const v = getGridMetricValue(s, metric);
    sampledValues.push(Number.isFinite(v) ? v : 0);
    if (Number.isFinite(v)) {
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
  }
  if (!Number.isFinite(minV) || !Number.isFinite(maxV) || Math.abs(maxV - minV) < 1e-9) {
    minV = 0;
    maxV = 1;
  }
  if (mode === "triangulated") {
    gridNodeCells.count = 0;
    updateTriangulatedGridSurface(
      gridSurfaceMesh,
      gridSurfaceMaterialFlat,
      gridSurfaceMaterialShaded,
      density,
      state,
      entries,
      sampledIndices,
      sampledValues,
      minV,
      maxV,
      metric,
      ramp,
      limit,
    );
    return;
  }
  const color = new THREE.Color();
  const dummy = new THREE.Object3D();
  let write = 0;
  for (let si = 0; si < sampledIndices.length; si += 1) {
    const i = sampledIndices[si];
    const idx = Number(entries[i][0]);
    const col = idx % density.cols;
    const row = Math.floor(idx / density.cols);
    const x = density.minX + (col + 0.5) * density.cellSize;
    const z = density.minZ + (row + 0.5) * density.cellSize;
    const s = entries[i][1];
    const y = getGridNodeElevation(s, metric) + SOUNDING_RENDER_LIFT_M;
    const t = clamp01((sampledValues[si] - minV) / Math.max(1e-6, maxV - minV));
    const rgb = sampleColorRamp(ramp, t);
    color.setRGB(rgb[0], rgb[1], rgb[2]);
    dummy.position.set(x, y, z);
    const cellSize = Math.max(0.2, Number(density.cellSize) || 1);
    // Plane was pre-rotated onto XZ; scale X and Z for full-cell footprint.
    dummy.scale.set(cellSize, 1, cellSize);
    dummy.rotation.set(0, 0, 0);
    dummy.updateMatrix();
    gridNodeCells.setMatrixAt(write, dummy.matrix);
    gridNodeCells.setColorAt(write, color);
    write += 1;
  }
  gridNodeCells.count = write;
  gridNodeCells.instanceMatrix.needsUpdate = true;
  if (gridNodeCells.instanceColor) {
    gridNodeCells.instanceColor.needsUpdate = true;
  }
  gridNodeCellMaterial.opacity = clamp01(Number(state.gridNodeOpacity) || 0.8);
  gridNodeCellMaterial.transparent = gridNodeCellMaterial.opacity < 0.999;
  gridSurfaceMesh.geometry.dispose();
  gridSurfaceMesh.geometry = new THREE.BufferGeometry();
}

function renderCoarseAggregatedTiles(
  gridNodeCells,
  gridNodeCellMaterial,
  gridSurfaceMesh,
  density,
  state,
  entries,
  metric,
  ramp,
  limit,
) {
  const ratio = entries.length / Math.max(1, limit);
  const blockStep = Math.max(2, Math.ceil(Math.sqrt(Math.max(1, ratio))));
  const blockCols = Math.max(1, Math.ceil(density.cols / blockStep));
  const blocks = new Map();
  for (let i = 0; i < entries.length; i += 1) {
    const idx = Number(entries[i][0]);
    const stats = entries[i][1];
    if (!Number.isFinite(idx) || !stats) continue;
    const col = idx % density.cols;
    const row = Math.floor(idx / density.cols);
    if (col < 0 || row < 0 || col >= density.cols || row >= density.rows) continue;
    const bc = Math.floor(col / blockStep);
    const br = Math.floor(row / blockStep);
    const bKey = br * blockCols + bc;
    let b = blocks.get(bKey);
    if (!b) {
      b = {
        bc,
        br,
        n: 0,
        metricSum: 0,
        elevSum: 0,
      };
      blocks.set(bKey, b);
    }
    b.n += 1;
    b.metricSum += Number(getGridMetricValue(stats, metric)) || 0;
    b.elevSum += Number(getGridNodeElevation(stats, metric)) || 0;
  }

  let minV = Infinity;
  let maxV = -Infinity;
  blocks.forEach((b) => {
    const v = b.n > 0 ? (b.metricSum / b.n) : 0;
    b.v = v;
    if (v < minV) minV = v;
    if (v > maxV) maxV = v;
  });
  if (!Number.isFinite(minV) || !Number.isFinite(maxV) || Math.abs(maxV - minV) < 1e-9) {
    minV = 0;
    maxV = 1;
  }

  const color = new THREE.Color();
  const dummy = new THREE.Object3D();
  let write = 0;
  const maxInstances = Math.max(
    1,
    Math.floor((gridNodeCells.instanceMatrix?.array?.length || 0) / 16),
  );
  blocks.forEach((b) => {
    if (write >= maxInstances) {
      return;
    }
    const stepCols = Math.max(1, Math.min(blockStep, density.cols - (b.bc * blockStep)));
    const stepRows = Math.max(1, Math.min(blockStep, density.rows - (b.br * blockStep)));
    const sizeX = stepCols * density.cellSize;
    const sizeZ = stepRows * density.cellSize;
    const x = density.minX + ((b.bc * blockStep) + (stepCols * 0.5)) * density.cellSize;
    const z = density.minZ + ((b.br * blockStep) + (stepRows * 0.5)) * density.cellSize;
    const y = ((b.n > 0 ? (b.elevSum / b.n) : 0) + SOUNDING_RENDER_LIFT_M);
    const t = clamp01((b.v - minV) / Math.max(1e-6, maxV - minV));
    const rgb = sampleColorRamp(ramp, t);
    color.setRGB(rgb[0], rgb[1], rgb[2]);
    dummy.position.set(x, y, z);
    dummy.scale.set(sizeX, 1, sizeZ);
    dummy.rotation.set(0, 0, 0);
    dummy.updateMatrix();
    gridNodeCells.setMatrixAt(write, dummy.matrix);
    gridNodeCells.setColorAt(write, color);
    write += 1;
  });
  gridNodeCells.count = write;
  gridNodeCells.instanceMatrix.needsUpdate = true;
  if (gridNodeCells.instanceColor) {
    gridNodeCells.instanceColor.needsUpdate = true;
  }
  gridNodeCellMaterial.opacity = clamp01(Number(state.gridNodeOpacity) || 0.8);
  gridNodeCellMaterial.transparent = gridNodeCellMaterial.opacity < 0.999;
  gridSurfaceMesh.geometry.dispose();
  gridSurfaceMesh.geometry = new THREE.BufferGeometry();
}

function updateTriangulatedGridSurface(
  gridSurfaceMesh,
  gridSurfaceMaterialFlat,
  gridSurfaceMaterialShaded,
  density,
  state,
  entries,
  sampledIndices,
  sampledValues,
  minV,
  maxV,
  metric,
  ramp,
  limit,
) {
  const statsByIdx = density.stats;
  const rows = Math.max(1, Number(density.rows) || 1);
  const cols = Math.max(1, Number(density.cols) || 1);
  const maskNoData = state.gridSurfaceMaskNoData !== false;
  const maxTris = Math.min(30000, Math.max(2, Math.floor(limit * 0.5)));
  const positions = [];
  const colors = [];
  const color = new THREE.Color();
  const valueByIdx = new Map();
  for (let i = 0; i < sampledIndices.length; i += 1) {
    const entryIndex = sampledIndices[i];
    valueByIdx.set(Number(entries[entryIndex][0]), sampledValues[i]);
  }
  const populated = Array.from(statsByIdx.keys());
  const targetCells = Math.max(1, Math.floor(maxTris / 2));
  const sampledPopulatedPositions = samplePositionsByHashedModulo(
    populated.length,
    targetCells,
    (i) => Number(populated[i]) || 0,
  );
  let triCount = 0;
  for (let s = 0; s < sampledPopulatedPositions.length && triCount < maxTris; s += 1) {
      const k = sampledPopulatedPositions[s];
      const i00 = Number(populated[k]);
      if (!Number.isFinite(i00) || i00 < 0) {
        continue;
      }
      const c = i00 % cols;
      const r = Math.floor(i00 / cols);
      if (r < 0 || c < 0 || r >= rows - 1 || c >= cols - 1) {
        continue;
      }
      const i10 = i00 + 1;
      const i01 = i00 + cols;
      const i11 = i01 + 1;
      const s00 = statsByIdx.get(i00);
      const s10 = statsByIdx.get(i10);
      const s01 = statsByIdx.get(i01);
      const s11 = statsByIdx.get(i11);
      if (maskNoData && (!s00 || !s10 || !s01 || !s11)) {
        continue;
      }
      if (!s00 || !s10 || !s01 || !s11) {
        continue;
      }
      const p00 = gridNodeVertex(density, c, r, s00, metric);
      const p10 = gridNodeVertex(density, c + 1, r, s10, metric);
      const p01 = gridNodeVertex(density, c, r + 1, s01, metric);
      const p11 = gridNodeVertex(density, c + 1, r + 1, s11, metric);
      const v00 = getMetricValueByIndex(i00, s00, valueByIdx, metric);
      const v10 = getMetricValueByIndex(i10, s10, valueByIdx, metric);
      const v01 = getMetricValueByIndex(i01, s01, valueByIdx, metric);
      const v11 = getMetricValueByIndex(i11, s11, valueByIdx, metric);
      pushColoredTri(positions, colors, p00, p10, p11, v00, v10, v11, minV, maxV, ramp, color);
      triCount += 1;
      if (triCount >= maxTris) break;
      pushColoredTri(positions, colors, p00, p11, p01, v00, v11, v01, minV, maxV, ramp, color);
      triCount += 1;
  }
  gridSurfaceMesh.geometry.dispose();
  const geom = new THREE.BufferGeometry();
  if (positions.length > 0) {
    geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geom.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    geom.computeVertexNormals();
  }
  gridSurfaceMesh.geometry = geom;
  const hillshade = state.gridSurfaceHillshade !== false;
  gridSurfaceMesh.material = hillshade ? gridSurfaceMaterialShaded : gridSurfaceMaterialFlat;
  gridSurfaceMesh.material.opacity = clamp01(Number(state.gridNodeOpacity) || 0.8);
  gridSurfaceMesh.material.transparent = gridSurfaceMesh.material.opacity < 0.999;
}

function getMetricValueByIndex(idx, stats, valueByIdx, metric) {
  const v = Number(valueByIdx.get(idx));
  if (Number.isFinite(v)) {
    return v;
  }
  return getGridMetricValue(stats, metric);
}

function samplePositionsByHashedModulo(length, targetCount, getKeyAt) {
  const n = Math.max(0, Number(length) || 0);
  const target = Math.max(1, Math.min(n, Math.floor(Number(targetCount) || 1)));
  if (n <= target) {
    const all = new Array(n);
    for (let i = 0; i < n; i += 1) all[i] = i;
    return all;
  }
  const bucket = Math.max(2, Math.ceil(n / target));
  const selected = [];
  const chosen = new Uint8Array(n);
  const phases = Math.min(bucket, 8);
  for (let phase = 0; phase < phases && selected.length < target; phase += 1) {
    for (let i = 0; i < n && selected.length < target; i += 1) {
      if (chosen[i]) continue;
      const key = Number(getKeyAt(i)) || 0;
      const h = hash32(key);
      if ((h % bucket) === phase) {
        chosen[i] = 1;
        selected.push(i);
      }
    }
  }
  if (selected.length < target) {
    const remaining = target - selected.length;
    const step = Math.max(1, Math.floor(n / Math.max(1, remaining)));
    for (let i = 0; i < n && selected.length < target; i += step) {
      if (chosen[i]) continue;
      chosen[i] = 1;
      selected.push(i);
    }
    for (let i = 0; i < n && selected.length < target; i += 1) {
      if (chosen[i]) continue;
      chosen[i] = 1;
      selected.push(i);
    }
  }
  return selected;
}

function hash32(v) {
  let x = (Number(v) | 0) ^ 0x9e3779b9;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return x >>> 0;
}

function gridNodeVertex(density, col, row, stats, metric) {
  const x = density.minX + (col + 0.5) * density.cellSize;
  const z = density.minZ + (row + 0.5) * density.cellSize;
  const y = getGridNodeElevation(stats, metric) + SOUNDING_RENDER_LIFT_M;
  return [x, y, z];
}

function pushColoredTri(positions, colors, a, b, c, va, vb, vc, minV, maxV, ramp, color) {
  positions.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
  const ta = clamp01((va - minV) / Math.max(1e-6, maxV - minV));
  const tb = clamp01((vb - minV) / Math.max(1e-6, maxV - minV));
  const tc = clamp01((vc - minV) / Math.max(1e-6, maxV - minV));
  const ra = sampleColorRamp(ramp, ta);
  const rb = sampleColorRamp(ramp, tb);
  const rc = sampleColorRamp(ramp, tc);
  color.setRGB(ra[0], ra[1], ra[2]);
  colors.push(color.r, color.g, color.b);
  color.setRGB(rb[0], rb[1], rb[2]);
  colors.push(color.r, color.g, color.b);
  color.setRGB(rc[0], rc[1], rc[2]);
  colors.push(color.r, color.g, color.b);
}

function getGridMetricValue(stats, metric) {
  if (!stats || typeof stats !== "object") {
    return NaN;
  }
  if (metric === "count") return Number(stats.count) || 0;
  if (metric === "min") return Number(stats.min);
  if (metric === "max") return Number(stats.max);
  if (metric === "stddev") return Number(stats.stddev) || 0;
  if (metric === "uncertainty") return Number(stats.uncertainty) || 0;
  return Number(stats.mean);
}

function getGridNodeElevation(stats, metric) {
  if (!stats || typeof stats !== "object") {
    return 0;
  }
  if (metric === "min") return Number(stats.min) || 0;
  if (metric === "max") return Number(stats.max) || 0;
  if (metric === "mean") return Number(stats.mean) || 0;
  return Number(stats.mean) || 0;
}

function buildDecimatedTriangleVerticesFromArray(arr, maxTriangles = 4000) {
  if (!arr || arr.length < 9) {
    return null;
  }
  const triangleCount = Math.floor(arr.length / 9);
  if (triangleCount <= 0) {
    return null;
  }
  const target = Math.max(1, Math.min(triangleCount, Math.floor(maxTriangles)));
  if (target >= triangleCount) {
    return new Float32Array(arr);
  }
  const out = new Float32Array(target * 9);
  const step = triangleCount / target;
  for (let k = 0; k < target; k += 1) {
    const tri = Math.min(triangleCount - 1, Math.floor((k + 0.5) * step));
    const src = tri * 9;
    const dst = k * 9;
    for (let i = 0; i < 9; i += 1) {
      out[dst + i] = arr[src + i];
    }
  }
  return out;
}

function createVessel(shape = "survey_launch") {
  if (shape === "catamaran") {
    return createCatamaranVessel();
  }
  if (shape === "auv") {
    return createAuvVessel();
  }
  if (shape === "marker") {
    return createMarkerVessel();
  }
  return createSurveyLaunchVessel();
}

function createSurveyLaunchVessel() {
  const group = new THREE.Group();

  const hull = new THREE.Mesh(
    new THREE.BoxGeometry(14, 3.5, 4.8),
    new THREE.MeshStandardMaterial({ color: 0xe6f1fa, metalness: 0.35, roughness: 0.45 }),
  );
  hull.castShadow = true;
  hull.receiveShadow = true;
  hull.position.y = 1.75;
  group.add(hull);

  const bow = new THREE.Mesh(
    new THREE.ConeGeometry(2.4, 4.4, 12),
    new THREE.MeshStandardMaterial({ color: 0xd7e9f9 }),
  );
  bow.rotation.z = -Math.PI / 2;
  bow.position.set(9, 1.9, 0);
  group.add(bow);

  const cabin = new THREE.Mesh(
    new THREE.BoxGeometry(4, 2.2, 3.4),
    new THREE.MeshStandardMaterial({ color: 0x4a89c6 }),
  );
  cabin.position.set(-1.5, 4, 0);
  group.add(cabin);

  const mast = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.1, 2.5, 8),
    new THREE.MeshStandardMaterial({ color: 0xdddddd }),
  );
  mast.position.set(-1.5, 5.8, 0);
  group.add(mast);

  const transducer = new THREE.Mesh(
    new THREE.CylinderGeometry(0.25, 0.25, 1.1, 16),
    new THREE.MeshStandardMaterial({ color: 0x50f0d1 }),
  );
  transducer.name = "transducer";
  transducer.position.set(0, 0.25, 0);
  group.add(transducer);

  return group;
}

function createCatamaranVessel() {
  const group = new THREE.Group();
  const materialHull = new THREE.MeshStandardMaterial({ color: 0xe6f1fa, metalness: 0.3, roughness: 0.48 });
  const hullPort = new THREE.Mesh(new THREE.BoxGeometry(14, 2.4, 1.8), materialHull);
  const hullStbd = hullPort.clone();
  hullPort.position.set(0, 1.2, -1.5);
  hullStbd.position.set(0, 1.2, 1.5);
  hullPort.castShadow = true;
  hullStbd.castShadow = true;
  group.add(hullPort, hullStbd);

  const deck = new THREE.Mesh(
    new THREE.BoxGeometry(7, 1.1, 4.2),
    new THREE.MeshStandardMaterial({ color: 0x5a93c9, metalness: 0.18, roughness: 0.56 }),
  );
  deck.position.set(-1, 2.55, 0);
  deck.castShadow = true;
  group.add(deck);

  const transducer = new THREE.Mesh(
    new THREE.CylinderGeometry(0.25, 0.25, 1.1, 16),
    new THREE.MeshStandardMaterial({ color: 0x50f0d1 }),
  );
  transducer.name = "transducer";
  transducer.position.set(0.5, 0.25, 0);
  group.add(transducer);
  return group;
}

function createAuvVessel() {
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.CapsuleGeometry(1.2, 10, 10, 20),
    new THREE.MeshStandardMaterial({ color: 0xe1edf8, metalness: 0.24, roughness: 0.42 }),
  );
  body.rotation.z = Math.PI * 0.5;
  body.position.y = 1.3;
  body.castShadow = true;
  group.add(body);

  const fin = new THREE.Mesh(
    new THREE.BoxGeometry(1.6, 0.1, 1.4),
    new THREE.MeshStandardMaterial({ color: 0x4a89c6 }),
  );
  fin.position.set(-1.4, 2.15, 0);
  group.add(fin);

  const transducer = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.2, 0.8, 16),
    new THREE.MeshStandardMaterial({ color: 0x50f0d1 }),
  );
  transducer.name = "transducer";
  transducer.position.set(1.6, 0.25, 0);
  group.add(transducer);
  return group;
}

function createMarkerVessel() {
  const group = new THREE.Group();
  const marker = new THREE.Mesh(
    new THREE.CylinderGeometry(0.9, 0.9, 1.4, 20),
    new THREE.MeshStandardMaterial({ color: 0xe6f1fa, metalness: 0.18, roughness: 0.5 }),
  );
  marker.position.y = 0.8;
  marker.castShadow = true;
  group.add(marker);

  const mast = new THREE.Mesh(
    new THREE.CylinderGeometry(0.06, 0.06, 2.2, 8),
    new THREE.MeshStandardMaterial({ color: 0x4a89c6 }),
  );
  mast.position.y = 2.2;
  group.add(mast);

  const transducer = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.2, 0.7, 16),
    new THREE.MeshStandardMaterial({ color: 0x50f0d1 }),
  );
  transducer.name = "transducer";
  transducer.position.set(0, 0.2, 0);
  group.add(transducer);
  return group;
}

function createSeafloor(preset, range) {
  const ext = getSeafloorExtents(preset);
  const terrain = preset === "etopo_aoi" ? getTerrainData() : null;
  if (terrain && getTerrainType(terrain) === "tin") {
    return createTinSeafloorMesh(terrain);
  }
  const width = Math.max(120, Math.abs(ext.maxX - ext.minX));
  const height = Math.max(120, Math.abs(ext.maxZ - ext.minZ));
  const segmentsX = terrain
    ? Math.max(24, Math.min(220, Number(terrain.width || 64) - 1))
    : (preset === "shipwreck_titanic" ? 520 : 220);
  const segmentsY = terrain
    ? Math.max(24, Math.min(220, Number(terrain.height || 64) - 1))
    : (preset === "shipwreck_titanic" ? 380 : 220);
  const geometry = new THREE.PlaneGeometry(width, height, segmentsX, segmentsY);
  const pos = geometry.attributes.position;

  for (let i = 0; i < pos.count; i += 1) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = sampleSeafloorHeight(preset, range, x, -y);
    pos.setZ(i, z);
  }

  geometry.computeVertexNormals();

  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      color: 0x4f6b55,
      roughness: 0.95,
      metalness: 0.06,
      side: THREE.DoubleSide,
    }),
  );
  mesh.receiveShadow = true;
  mesh.rotation.x = -Math.PI / 2;
  return mesh;
}

function createTinSeafloorMesh(terrain) {
  const vertices = terrain?.vertices;
  const triangles = terrain?.triangles;
  if (!vertices || !triangles || vertices.length < 9 || triangles.length < 3) {
    return createSeafloor("flat", 80);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.BufferAttribute(triangles, 1));
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      color: 0x4f6b55,
      roughness: 0.95,
      metalness: 0.06,
      side: THREE.DoubleSide,
    }),
  );
  mesh.receiveShadow = true;
  return mesh;
}

function getSeafloorExtents(preset) {
  const terrain = preset === "etopo_aoi" ? getTerrainData() : null;
  const terrainExt = getTerrainExtents(terrain);
  if (terrainExt) {
    return terrainExt;
  }
  if (preset === "shipwreck_titanic") {
    return { minX: -420, maxX: 980, minZ: -460, maxZ: 460 };
  }
  return { minX: -1400, maxX: 1400, minZ: -1400, maxZ: 1400 };
}

function updateMeshGridGeometry(meshGrid, state) {
  const geometry = meshGrid.geometry;
  const spacing = Math.max(1, Number(state.meshGridSpacing) || 4);
  if (!state.meshGridEnabled) {
    geometry.dispose();
    meshGrid.geometry = new THREE.BufferGeometry();
    return;
  }

  const ext = getSeafloorExtents(state.seafloorPreset);
  const spanX = Math.max(1, ext.maxX - ext.minX);
  const spanZ = Math.max(1, ext.maxZ - ext.minZ);
  const terrain = state.seafloorPreset === "etopo_aoi" ? getTerrainData() : null;
  const targetLines = 4096;
  const desiredLinesX = spanX / spacing + 1;
  const desiredLinesZ = spanZ / spacing + 1;
  const lineSkipX = Math.max(1, Math.ceil(desiredLinesX / targetLines));
  const lineSkipZ = Math.max(1, Math.ceil(desiredLinesZ / targetLines));
  const stepX = spacing * lineSkipX;
  const stepZ = spacing * lineSkipZ;
  const terrainStepX = terrain ? estimateTerrainStep(terrain, spanX, "x") : 0;
  const terrainStepZ = terrain ? estimateTerrainStep(terrain, spanZ, "z") : 0;
  const baseSampleStep = terrain
    ? Math.max(2, Math.min(80, Math.min(terrainStepX, terrainStepZ)))
    : Math.max(2, Math.min(14, spacing * 0.7));
  const sampleStepX = Math.max(2, baseSampleStep);
  const sampleStepZ = Math.max(2, baseSampleStep);
  const yOffset = 0.16;
  const positions = [];

  for (let x = snapUp(ext.minX, stepX); x <= ext.maxX + 1e-6; x += stepX) {
    appendDrapedPolyline(positions, true, x, ext.minZ, ext.maxZ, sampleStepZ, state, yOffset);
  }
  for (let z = snapUp(ext.minZ, stepZ); z <= ext.maxZ + 1e-6; z += stepZ) {
    appendDrapedPolyline(positions, false, z, ext.minX, ext.maxX, sampleStepX, state, yOffset);
  }

  geometry.dispose();
  meshGrid.geometry = new THREE.BufferGeometry();
  meshGrid.geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
}

function appendDrapedPolyline(out, alongZ, fixedValue, minVar, maxVar, sampleStep, state, yOffset) {
  const points = [];
  const terrain = state.seafloorPreset === "etopo_aoi" ? getTerrainData() : null;
  for (let v = minVar; v <= maxVar + 1e-6; v += sampleStep) {
    const x = alongZ ? fixedValue : v;
    const z = alongZ ? v : fixedValue;
    const yBase = terrain ? sampleTerrainHeight(terrain, x, z) : sampleSeafloorHeight(state.seafloorPreset, state.range, x, z);
    if (!Number.isFinite(yBase)) {
      points.push(null);
      continue;
    }
    points.push([x, yBase + yOffset, z]);
  }
  if (points.length < 2) {
    return;
  }
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    out.push(a[0], a[1], a[2], b[0], b[1], b[2]);
  }
}

function estimateTerrainStep(terrain, span, axis) {
  if (!terrain) return 0;
  const type = getTerrainType(terrain);
  if (type === "grid") {
    const count = axis === "x" ? Number(terrain.width || 2) : Number(terrain.height || 2);
    return span / Math.max(2, count - 1);
  }
  if (type === "tin") {
    const triCount = Math.max(1, Math.floor(Number(terrain.triangles?.length || 0) / 3));
    const vertexCount = Math.max(3, Math.floor(Number(terrain.vertices?.length || 0) / 3));
    const densityScale = Math.sqrt(Math.max(triCount, vertexCount));
    return span / Math.max(12, densityScale);
  }
  return 0;
}

function snapUp(value, step) {
  return Math.ceil(value / step) * step;
}

function updateFanGeometryFromSnapshot(snapshot, liveBuffers, state, depthWindow = null) {
  const hits = snapshot.hits || [];
  const compareHits = Array.isArray(snapshot.compareHits) ? snapshot.compareHits : [];
  if (!hits.length) {
    clearFanGeometry(liveBuffers);
    return { hits: [] };
  }
  const stride = getLiveDisplayHitStride(hits.length);
  const compareStride = getLiveDisplayHitStride(compareHits.length);
  const origin = snapshot.origin;
  const color = new THREE.Color();
  const fanPositions = liveBuffers.fanPositions;
  const rayPositions = liveBuffers.rayPositions;
  const soundingPositions = liveBuffers.soundingPositions;
  const soundingColors = liveBuffers.soundingColors;

  fanPositions[0] = origin[0];
  fanPositions[1] = origin[1];
  fanPositions[2] = origin[2];

  let liveCount = 0;
  let fanCount = 0;
  for (let i = 0; i < hits.length; i += stride) {
    const p = hits[i]?.point;
    if (!Array.isArray(p) || p.length < 3) {
      continue;
    }
    const next = liveCount + 1;
    if (next > liveBuffers.capacity) {
      break;
    }
    const fv = next * 3;
    fanPositions[fv] = p[0];
    fanPositions[fv + 1] = p[1];
    fanPositions[fv + 2] = p[2];

    const sv = liveCount * 3;
    soundingPositions[sv] = p[0];
    soundingPositions[sv + 1] = p[1] + SOUNDING_RENDER_LIFT_M;
    soundingPositions[sv + 2] = p[2];
    const depth = Math.max(0, -Number(p[1] || 0));
    getSoundingColorForDepth(depth, state, color, true, depthWindow);
    soundingColors[sv] = color.r;
    soundingColors[sv + 1] = color.g;
    soundingColors[sv + 2] = color.b;

    const rv = liveCount * 6;
    rayPositions[rv] = origin[0];
    rayPositions[rv + 1] = origin[1];
    rayPositions[rv + 2] = origin[2];
    rayPositions[rv + 3] = p[0];
    rayPositions[rv + 4] = p[1];
    rayPositions[rv + 5] = p[2];
    liveCount = next;
    fanCount = next;
  }

  for (let i = 0; i < compareHits.length; i += compareStride) {
    const p = compareHits[i]?.point;
    if (!Array.isArray(p) || p.length < 3) {
      continue;
    }
    if (liveCount >= liveBuffers.capacity) {
      break;
    }
    const sv = liveCount * 3;
    soundingPositions[sv] = p[0];
    soundingPositions[sv + 1] = p[1] + SOUNDING_RENDER_LIFT_M;
    soundingPositions[sv + 2] = p[2];
    const depth = Math.max(0, -Number(p[1] || 0));
    getSoundingColorForDepth(depth, state, color, true, depthWindow);
    // Slight tint shift for truth overlay in "both" mode.
    soundingColors[sv] = clamp01(color.r * 0.8 + 0.2);
    soundingColors[sv + 1] = clamp01(color.g * 0.9);
    soundingColors[sv + 2] = clamp01(color.b * 1.05);
    liveCount += 1;
  }

  liveBuffers.fanGeometry.setDrawRange(0, Math.max(0, (fanCount - 1) * 3));
  liveBuffers.rayGeometry.setDrawRange(0, fanCount * 2);
  liveBuffers.soundingGeometry.setDrawRange(0, liveCount);
  liveBuffers.fanPositionAttr.needsUpdate = true;
  liveBuffers.rayPositionAttr.needsUpdate = true;
  liveBuffers.soundingPositionAttr.needsUpdate = true;
  liveBuffers.soundingColorAttr.needsUpdate = true;
  liveBuffers.liveCount = liveCount;

  return { hits };
}

function appendSoundingHistoryChunked(
  hits,
  chunks,
  root,
  material,
  chunkCapacity,
  totalPoints,
  maxHistoryPoints,
  state,
  stride = 1,
  depthWindow = null,
) {
  const c = new THREE.Color();
  const solid = state.soundingColorMode === "solid";
  if (solid) {
    c.set(state.historySoundingsColor);
  }
  const step = Math.max(1, Math.floor(Number(stride) || 1));
  let touchedChunk = null;

  for (let i = 0; i < hits.length; i += step) {
    const p = hits[i]?.point;
    if (!Array.isArray(p) || p.length < 3) {
      continue;
    }
    let chunk = chunks[chunks.length - 1];
    if (!chunk || chunk.write >= chunk.capacity) {
      if (touchedChunk) {
        touchedChunk.geometry.setDrawRange(touchedChunk.start, touchedChunk.count);
        touchedChunk.positionAttr.needsUpdate = true;
        touchedChunk.colorAttr.needsUpdate = true;
      }
      chunk = createHistoryChunk(chunkCapacity, material);
      chunks.push(chunk);
      root.add(chunk.points);
      touchedChunk = chunk;
    }
    if (touchedChunk && touchedChunk !== chunk) {
      touchedChunk.geometry.setDrawRange(touchedChunk.start, touchedChunk.count);
      touchedChunk.positionAttr.needsUpdate = true;
      touchedChunk.colorAttr.needsUpdate = true;
    }
    touchedChunk = chunk;
    const j = chunk.write * 3;
    chunk.positions[j] = p[0];
    chunk.positions[j + 1] = p[1] + SOUNDING_RENDER_LIFT_M;
    chunk.positions[j + 2] = p[2];
    if (solid) {
      chunk.colors[j] = c.r;
      chunk.colors[j + 1] = c.g;
      chunk.colors[j + 2] = c.b;
    } else {
      const depth = Math.max(0, -Number(p[1] || 0));
      getSoundingColorForDepth(depth, state, c, false, depthWindow);
      chunk.colors[j] = c.r;
      chunk.colors[j + 1] = c.g;
      chunk.colors[j + 2] = c.b;
    }
    chunk.write += 1;
    chunk.count += 1;
    totalPoints += 1;
  }
  if (touchedChunk) {
    touchedChunk.geometry.setDrawRange(touchedChunk.start, touchedChunk.count);
    touchedChunk.positionAttr.needsUpdate = true;
    touchedChunk.colorAttr.needsUpdate = true;
  }

  if (Number.isFinite(maxHistoryPoints)) {
    totalPoints = trimHistoryChunks(chunks, root, totalPoints, Math.max(0, Math.floor(maxHistoryPoints)));
  }
  return totalPoints;
}

function createHistoryChunk(capacity, material) {
  const positions = new Float32Array(capacity * 3);
  const colors = new Float32Array(capacity * 3);
  const geometry = new THREE.BufferGeometry();
  const positionAttr = new THREE.BufferAttribute(positions, 3);
  const colorAttr = new THREE.BufferAttribute(colors, 3);
  positionAttr.setUsage(THREE.DynamicDrawUsage);
  colorAttr.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute("position", positionAttr);
  geometry.setAttribute("color", colorAttr);
  geometry.setDrawRange(0, 0);
  const points = new THREE.Points(geometry, material);
  return { points, geometry, positions, colors, positionAttr, colorAttr, start: 0, write: 0, count: 0, capacity };
}

function trimHistoryChunks(chunks, root, totalPoints, maxPoints) {
  if (maxPoints <= 0) {
    clearSoundingHistory(chunks, root);
    return 0;
  }
  while (totalPoints > maxPoints && chunks.length > 0) {
    const overflow = totalPoints - maxPoints;
    const oldest = chunks[0];
    if (overflow >= oldest.count) {
      totalPoints -= oldest.count;
      disposeHistoryChunk(oldest, root);
      chunks.shift();
      continue;
    }
    const removePoints = overflow;
    oldest.start += removePoints;
    oldest.count -= removePoints;
    oldest.geometry.setDrawRange(oldest.start, oldest.count);
    totalPoints -= removePoints;
  }
  return totalPoints;
}

function recolorHistoryChunks(chunks, state, depthWindow = null) {
  if (!chunks.length) {
    return;
  }
  const c = new THREE.Color();
  const solid = state.soundingColorMode === "solid";
  if (solid) {
    c.set(state.historySoundingsColor);
  }
  for (let i = 0; i < chunks.length; i += 1) {
    const chunk = chunks[i];
    const end = chunk.start + chunk.count;
    for (let p = chunk.start; p < end; p += 1) {
      const idx = p * 3;
      if (solid) {
        chunk.colors[idx] = c.r;
        chunk.colors[idx + 1] = c.g;
        chunk.colors[idx + 2] = c.b;
      } else {
        const depth = Math.max(0, -Number(chunk.positions[idx + 1] || 0));
        getSoundingColorForDepth(depth, state, c, false, depthWindow);
        chunk.colors[idx] = c.r;
        chunk.colors[idx + 1] = c.g;
        chunk.colors[idx + 2] = c.b;
      }
    }
    chunk.colorAttr.needsUpdate = true;
  }
}

function recolorLiveSoundings(liveBuffers, state, depthWindow = null) {
  const count = Math.max(0, Math.floor(Number(liveBuffers?.liveCount) || 0));
  if (!count) {
    return;
  }
  const c = new THREE.Color();
  const solid = state.soundingColorMode === "solid";
  if (solid) {
    c.set(state.soundingPointsColor);
  }
  for (let i = 0; i < count; i += 1) {
    const idx = i * 3;
    if (solid) {
      liveBuffers.soundingColors[idx] = c.r;
      liveBuffers.soundingColors[idx + 1] = c.g;
      liveBuffers.soundingColors[idx + 2] = c.b;
      continue;
    }
    const depth = Math.max(0, -(Number(liveBuffers.soundingPositions[idx + 1]) - SOUNDING_RENDER_LIFT_M || 0));
    getSoundingColorForDepth(depth, state, c, true, depthWindow);
    liveBuffers.soundingColors[idx] = c.r;
    liveBuffers.soundingColors[idx + 1] = c.g;
    liveBuffers.soundingColors[idx + 2] = c.b;
  }
  liveBuffers.soundingColorAttr.needsUpdate = true;
}

function clearSoundingHistory(chunks, root) {
  while (chunks.length) {
    disposeHistoryChunk(chunks.pop(), root);
  }
}

function disposeHistoryChunk(chunk, root) {
  if (!chunk) {
    return;
  }
  root.remove(chunk.points);
  chunk.geometry.dispose();
}

function getSoundingColorForDepth(depthMeters, state, color, useCurrentSolid = false, depthWindow = null) {
  const d = Math.max(0, Number(depthMeters) || 0);
  if (state.soundingColorMode === "solid") {
    color.set(useCurrentSolid ? state.soundingPointsColor : state.historySoundingsColor);
    return color;
  }
  const [dMin, dMax] = depthWindow || getSoundingDepthWindow(state);
  let t = (d - dMin) / Math.max(1e-6, dMax - dMin);
  t = clamp01(t);
  if (state.soundingColorInvert) {
    t = 1 - t;
  }
  const [r, g, b] = sampleColorRamp(state.soundingColorRamp, t);
  color.setRGB(r, g, b);
  return color;
}

function getSoundingDepthWindow(state, liveBuffers = null, historyChunks = null) {
  if (state.soundingDepthMode === "fixed") {
    const dMin = Math.max(0, Number(state.soundingDepthMin) || 0);
    const dMaxRaw = Number(state.soundingDepthMax);
    const dMax = Math.max(dMin + 1, Number.isFinite(dMaxRaw) ? dMaxRaw : dMin + 100);
    return [dMin, dMax];
  }
  const autoWindow = getAutoSoundingDepthWindow(liveBuffers, historyChunks);
  if (autoWindow) {
    return autoWindow;
  }
  return [0, Math.max(1, Number(state.range) || 1)];
}

function getAutoSoundingDepthWindow(liveBuffers, historyChunks) {
  let minDepth = Infinity;
  let maxDepth = -Infinity;

  const liveCount = Math.max(0, Math.floor(Number(liveBuffers?.liveCount) || 0));
  for (let i = 0; i < liveCount; i += 1) {
    const depth = Math.max(0, -(Number(liveBuffers.soundingPositions[(i * 3) + 1]) - SOUNDING_RENDER_LIFT_M || 0));
    if (!Number.isFinite(depth)) {
      continue;
    }
    if (depth < minDepth) minDepth = depth;
    if (depth > maxDepth) maxDepth = depth;
  }

  if (Array.isArray(historyChunks)) {
    for (let i = 0; i < historyChunks.length; i += 1) {
      const chunk = historyChunks[i];
      const end = chunk.start + chunk.count;
      for (let p = chunk.start; p < end; p += 1) {
        const depth = Math.max(0, -(Number(chunk.positions[(p * 3) + 1]) - SOUNDING_RENDER_LIFT_M || 0));
        if (!Number.isFinite(depth)) {
          continue;
        }
        if (depth < minDepth) minDepth = depth;
        if (depth > maxDepth) maxDepth = depth;
      }
    }
  }

  if (!Number.isFinite(minDepth) || !Number.isFinite(maxDepth)) {
    return null;
  }
  if (Math.abs(maxDepth - minDepth) < 1e-3) {
    return [Math.max(0, minDepth - 0.5), minDepth + 0.5];
  }
  const pad = Math.max(0.5, (maxDepth - minDepth) * 0.05);
  return [Math.max(0, minDepth - pad), maxDepth + pad];
}

function sampleColorRamp(name, t) {
  const x = clamp01(Number(t) || 0);
  if (name === "gray") {
    return [x, x, x];
  }
  if (name === "viridis") {
    return lerpRamp(
      x,
      [
        [0.267, 0.005, 0.329],
        [0.283, 0.141, 0.458],
        [0.254, 0.265, 0.53],
        [0.207, 0.372, 0.553],
        [0.164, 0.471, 0.558],
        [0.128, 0.567, 0.551],
        [0.135, 0.659, 0.518],
        [0.267, 0.749, 0.441],
        [0.478, 0.821, 0.318],
        [0.741, 0.873, 0.15],
      ],
    );
  }
  if (name === "turbo") {
    return lerpRamp(
      x,
      [
        [0.19, 0.071, 0.232],
        [0.275, 0.235, 0.692],
        [0.213, 0.465, 0.958],
        [0.115, 0.701, 0.796],
        [0.457, 0.859, 0.439],
        [0.84, 0.897, 0.196],
        [0.978, 0.628, 0.141],
        [0.886, 0.26, 0.196],
      ],
    );
  }
  return lerpRamp(
    x,
    [
      [0.2, 0.69, 0.93],
      [0.28, 0.86, 0.83],
      [0.66, 0.93, 0.57],
      [0.99, 0.79, 0.36],
      [0.96, 0.43, 0.23],
    ],
  );
}

function lerpRamp(t, stops) {
  if (!Array.isArray(stops) || stops.length < 2) {
    return [1, 1, 1];
  }
  const n = stops.length - 1;
  const u = clamp01(t) * n;
  const i = Math.min(n - 1, Math.floor(u));
  const f = u - i;
  const a = stops[i];
  const b = stops[i + 1];
  return [
    a[0] + (b[0] - a[0]) * f,
    a[1] + (b[1] - a[1]) * f,
    a[2] + (b[2] - a[2]) * f,
  ];
}

function getSoundingColorKey(state, depthWindow = null) {
  const autoWindow = state.soundingDepthMode === "auto" && Array.isArray(depthWindow)
    ? `${depthWindow[0].toFixed(3)}:${depthWindow[1].toFixed(3)}`
    : "fixed";
  return [
    state.soundingColorMode,
    state.soundingColorRamp,
    state.soundingColorInvert ? 1 : 0,
    state.soundingDepthMode,
    Number(state.soundingDepthMin) || 0,
    Number(state.soundingDepthMax) || 0,
    Number(state.range) || 0,
    autoWindow,
    state.soundingPointsColor || "#fff",
    state.historySoundingsColor || "#fff",
  ].join("|");
}

function clearFanGeometry(liveBuffers) {
  liveBuffers.fanGeometry.setDrawRange(0, 0);
  liveBuffers.rayGeometry.setDrawRange(0, 0);
  liveBuffers.soundingGeometry.setDrawRange(0, 0);
  liveBuffers.liveCount = 0;
  liveBuffers.fanPositionAttr.needsUpdate = true;
  liveBuffers.rayPositionAttr.needsUpdate = true;
  liveBuffers.soundingPositionAttr.needsUpdate = true;
  liveBuffers.soundingColorAttr.needsUpdate = true;
}

function createLivePingBuffers(fanMesh, fanRays, soundingPoints, maxHits = 2048) {
  const capacity = Math.max(64, Math.floor(Number(maxHits) || 2048));
  const fanPositions = new Float32Array((capacity + 1) * 3);
  const rayPositions = new Float32Array(capacity * 6);
  const soundingPositions = new Float32Array(capacity * 3);
  const soundingColors = new Float32Array(capacity * 3);
  const fanIndices = new Uint32Array(Math.max(0, (capacity - 1) * 3));
  for (let i = 1; i < capacity; i += 1) {
    const j = (i - 1) * 3;
    fanIndices[j] = 0;
    fanIndices[j + 1] = i;
    fanIndices[j + 2] = i + 1;
  }

  const fanGeometry = new THREE.BufferGeometry();
  const rayGeometry = new THREE.BufferGeometry();
  const soundingGeometry = new THREE.BufferGeometry();
  const fanPositionAttr = new THREE.BufferAttribute(fanPositions, 3);
  const rayPositionAttr = new THREE.BufferAttribute(rayPositions, 3);
  const soundingPositionAttr = new THREE.BufferAttribute(soundingPositions, 3);
  const soundingColorAttr = new THREE.BufferAttribute(soundingColors, 3);
  fanPositionAttr.setUsage(THREE.DynamicDrawUsage);
  rayPositionAttr.setUsage(THREE.DynamicDrawUsage);
  soundingPositionAttr.setUsage(THREE.DynamicDrawUsage);
  soundingColorAttr.setUsage(THREE.DynamicDrawUsage);
  fanGeometry.setAttribute("position", fanPositionAttr);
  fanGeometry.setIndex(new THREE.BufferAttribute(fanIndices, 1));
  fanGeometry.setDrawRange(0, 0);
  rayGeometry.setAttribute("position", rayPositionAttr);
  rayGeometry.setDrawRange(0, 0);
  soundingGeometry.setAttribute("position", soundingPositionAttr);
  soundingGeometry.setAttribute("color", soundingColorAttr);
  soundingGeometry.setDrawRange(0, 0);
  fanMesh.geometry.dispose();
  fanMesh.geometry = fanGeometry;
  fanMesh.frustumCulled = false;
  fanRays.geometry.dispose();
  fanRays.geometry = rayGeometry;
  fanRays.frustumCulled = false;
  soundingPoints.geometry.dispose();
  soundingPoints.geometry = soundingGeometry;
  soundingPoints.frustumCulled = false;

  return {
    capacity,
    liveCount: 0,
    fanPositions,
    rayPositions,
    soundingPositions,
    soundingColors,
    fanGeometry,
    rayGeometry,
    soundingGeometry,
    fanPositionAttr,
    rayPositionAttr,
    soundingPositionAttr,
    soundingColorAttr,
  };
}

function getLiveDisplayHitStride(hitCount) {
  const maxLiveHits = 1024;
  return hitCount > maxLiveHits ? Math.max(1, Math.ceil(hitCount / maxLiveHits)) : 1;
}

function getHistoryDisplayHitStride(hitCount) {
  const maxHistoryHitsPerPing = 768;
  return hitCount > maxHistoryHitsPerPing ? Math.max(1, Math.ceil(hitCount / maxHistoryHitsPerPing)) : 1;
}

function updateMissionPlanGeometry(lineSegments, segments) {
  const points = [];
  const colors = [];
  const mainColor = new THREE.Color(0x8ff3c8);
  const infillColor = new THREE.Color(0xffca7a);
  if (Array.isArray(segments)) {
    for (let i = 0; i < segments.length; i += 1) {
      const s = segments[i];
      const a = s?.start;
      const b = s?.end;
      if (!Array.isArray(a) || !Array.isArray(b)) {
        continue;
      }
      points.push(a[0], 0.12, a[1], b[0], 0.12, b[1]);
      const c = s?.kind === "infill" ? infillColor : mainColor;
      colors.push(c.r, c.g, c.b, c.r, c.g, c.b);
    }
  }
  lineSegments.geometry.dispose();
  lineSegments.geometry = new THREE.BufferGeometry();
  if (points.length) {
    lineSegments.geometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
    lineSegments.geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  }
}

function updateBeamFootprints(state, hits, footprintFill, footprintRing) {
  const dummy = new THREE.Object3D();
  const across = hits[hits.length - 1].point.clone().sub(hits[0].point);
  const acrossDir = across.lengthSq() > 1e-6 ? across.normalize() : new THREE.Vector3(1, 0, 0);
  const baseMinor = Math.max((state.soundVelocity * (state.pulseWidth / 1000)) / 2, 0.2);
  const beamAngleRad = degToRad(state.beamAngle);
  const perBeamAngle = beamAngleRad / Math.max(state.beamCount, 1);

  const count = Math.min(hits.length, footprintFill.instanceMatrix.count);
  footprintFill.count = count;
  footprintRing.count = count;

  for (let i = 0; i < count; i += 1) {
    const hit = hits[i];
    const normal = hit.normal.clone().normalize();
    let alongDir = new THREE.Vector3().crossVectors(normal, acrossDir).normalize();
    if (alongDir.lengthSq() < 1e-6) {
      alongDir = new THREE.Vector3(0, 0, 1);
    }
    const localRange = Math.max(Math.abs(hit.point.y), 1);
    const baseMajor = Math.max(localRange * Math.tan(perBeamAngle * 0.5), 0.15);
    const edgeBoost = 1 + Math.abs(hit.u) * 0.6;
    const radius = Math.max(baseMajor, baseMinor) * edgeBoost;

    const basis = new THREE.Matrix4().makeBasis(acrossDir, alongDir, normal);
    dummy.quaternion.setFromRotationMatrix(basis);
    dummy.position.copy(hit.point).addScaledVector(normal, 0.04);
    dummy.scale.set(radius, radius, 1);
    dummy.updateMatrix();
    footprintFill.setMatrixAt(i, dummy.matrix);
    footprintRing.setMatrixAt(i, dummy.matrix);
  }

  footprintFill.instanceMatrix.needsUpdate = true;
  footprintRing.instanceMatrix.needsUpdate = true;
}

function setVesselColor(vessel, hexColor) {
  if (vessel.userData.baseColor === hexColor) {
    return;
  }
  vessel.userData.baseColor = hexColor;
  vessel.traverse((obj) => {
    if (!obj.isMesh || !obj.material || !obj.material.color) {
      return;
    }
    obj.material.color.set(hexColor);
  });
}

function setVesselOpacity(vessel, opacity) {
  vessel.traverse((obj) => {
    if (!obj.isMesh || !obj.material) {
      return;
    }
    obj.material.opacity = opacity;
    obj.material.transparent = opacity < 0.999;
  });
}

function disposeGroupMaterials(group) {
  group.traverse((obj) => {
    if (!obj.isMesh) {
      return;
    }
    obj.geometry?.dispose?.();
    if (Array.isArray(obj.material)) {
      obj.material.forEach((m) => m?.dispose?.());
    } else {
      obj.material?.dispose?.();
    }
  });
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

function createCircleSpriteTexture() {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, size, size);
  const r = size * 0.42;
  const cx = size * 0.5;
  const cy = size * 0.5;
  const grad = ctx.createRadialGradient(cx, cy, r * 0.25, cx, cy, r);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}
